import { useState } from 'react';
import { Search, Settings, List, LayoutGrid, Copy, Trash2, RotateCcw, Save, SlidersHorizontal, Zap, Check, X, Loader2, Sparkles, Plus } from 'lucide-react';
import { Card } from '../App';
import { fetchCardData, searchCards, getCardImageUrl, type ScryfallCard } from '../lib/scryfall';
import { ManaSymbol } from './ManaSymbol';
import { BrewTooltip } from './BrewTooltip';
import { NumericStepper } from './NumericStepper';

interface DeckStats {
  weight: number;
  targetWeight: number;
  curve: number;
  targetCurve: number;
  actualRatio?: number;
  nonlands: number;
  targetNonlands: number;
  lands: number;
  sideboard: number;
  mainboard: number;
  extraCards?: number;
  // Plan-specific stats for actual/plan/target comparison
  planStats?: {
    mainboard: number;
    lands: number;
    spells: number;
    weight: number;
    curve: number;
    ratio: number;
    sideboard: number;
  };
}

interface ToolbarControls {
  targetDeckSize: number;
  setTargetDeckSize: (size: number | null) => void;
  effectiveTargetLands: number;
  displayLandCount: number;
  setTargetLands: (lands: number | null) => void;
  buildTargetRatio: number;
  setTargetRatio: (ratio: number) => void;
  mvParity: 'all' | 'odds' | 'evens';
  setMvParity: (parity: 'all' | 'odds' | 'evens' | ((prev: 'all' | 'odds' | 'evens') => 'all' | 'odds' | 'evens')) => void;
  excludeZero: boolean;
  setExcludeZero: (val: boolean | ((prev: boolean) => boolean)) => void;
  groupBy: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setGroupBy: (val: any) => void;
  activePlan: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setActivePlan: (val: any) => void;
  viewMode: string;
  setViewMode: (val: 'dynamic' | 'list' | 'grid') => void;
  onThePlay: boolean;
  setOnThePlay: (val: boolean) => void;
  probTargetTurn: number;
  setProbTargetTurn: (val: number) => void;
  copyToClipboard: () => void;
  clearDeck?: () => void;
  clearPlan?: () => void;
  deleteDeck?: () => void;
  saveDeck?: () => void;
}

interface CardSearchProps {
  onAddCard: (card: Card) => void;
  onAddToSideboard?: (card: Card) => void;
  onShowImport?: () => void;
  stats?: DeckStats;
  toolbar?: ToolbarControls;
  deck?: Card[];
  format?: string;
  onFormatChange?: (format: string) => void;
  autoSuggestLands?: boolean;
  onToggleAutoSuggestLands?: (val: boolean) => void;
  onAutoSuggestLands?: () => void;
}

export function CardSearch({
  onAddCard,
  onAddToSideboard,
  onShowImport,
  stats,
  toolbar,
  deck = [],
  format = 'Standard',
  onFormatChange,
  autoSuggestLands = false,
  onToggleAutoSuggestLands,
}: CardSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedFeedback, setCopiedFeedback] = useState(false);

  // Collapsible drawers
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTuning, setShowTuning] = useState(false);

  // Advanced search state
  const [colorFilter, setColorFilter] = useState<string[]>([]);
  const [colorMatchMode, setColorMatchMode] = useState<'identity' | 'includes' | 'exact'>('identity');
  const [rarityFilter, setRarityFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [oracleFilter, setOracleFilter] = useState('');
  const [mvOperator, setMvOperator] = useState<'=' | '<=' | '>='>('=');
  const [cmcFilter, setCmcFilter] = useState('');
  const [formatFilter, setFormatFilter] = useState('');
  const [sortOrder, setSortOrder] = useState<'edhrec' | 'cmc' | 'name' | 'usd'>('edhrec');
  const [advResults, setAdvResults] = useState<ScryfallCard[] | null>(null);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [addedResultKey, setAddedResultKey] = useState<string | null>(null);

  const activeFilterCount =
    (colorFilter.length > 0 ? 1 : 0) +
    (rarityFilter.length > 0 ? 1 : 0) +
    (typeFilter.trim() ? 1 : 0) +
    (oracleFilter.trim() ? 1 : 0) +
    (cmcFilter.trim() ? 1 : 0) +
    (formatFilter.trim() ? 1 : 0);

  const deckColors = Array.from(
    new Set(
      deck.flatMap((c) => (c.colorIdentity && c.colorIdentity.length > 0 ? c.colorIdentity : c.colors || []))
    )
  ).filter((c) => ['W', 'U', 'B', 'R', 'G'].includes(c));

  const buildScryfallFilters = () => {
    let formatKey = '';
    if (formatFilter === 'any') {
      formatKey = '';
    } else if (formatFilter.trim()) {
      formatKey = formatFilter.trim().toLowerCase();
    } else if (format && format !== 'Auto') {
      formatKey = format.toLowerCase().includes('commander') || format.toLowerCase() === 'edh'
        ? 'commander'
        : format.toLowerCase();
    }

    const filters: string[] = [];
    if (colorFilter.length > 0) {
      const colorQuery = colorFilter.map((c) => (c === 'C' ? 'c' : c.toLowerCase())).join('');
      if (colorMatchMode === 'identity') {
        filters.push(`id<=${colorQuery}`);
      } else if (colorMatchMode === 'includes') {
        filters.push(colorQuery === 'c' ? 'c:c' : `c>=${colorQuery.replace(/c/g, '')}`);
      } else {
        filters.push(colorQuery === 'c' ? 'c:c' : `c=${colorQuery.replace(/c/g, '')}`);
      }
    }
    if (typeFilter.trim()) {
      const typeTokens = typeFilter
        .trim()
        .split(/[\s,]+/)
        .filter(Boolean);
      typeTokens.forEach((tok) => filters.push(`t:${tok}`));
    }
    if (oracleFilter.trim()) {
      filters.push(`o:"${oracleFilter.trim()}"`);
    }
    if (cmcFilter.trim()) {
      const rawMv = cmcFilter.trim();
      if (/^[<>=!]/.test(rawMv)) {
        filters.push(`mv${rawMv}`);
      } else if (rawMv.endsWith('+')) {
        filters.push(`mv>=${rawMv.slice(0, -1)}`);
      } else {
        filters.push(`mv${mvOperator === '=' ? ':' : mvOperator}${rawMv}`);
      }
    }
    if (rarityFilter.length > 0) {
      const rarityTerms = rarityFilter
        .map((r) =>
          r === 'C'
            ? 'r:common'
            : r === 'U'
            ? 'r:uncommon'
            : r === 'R'
            ? 'r:rare'
            : r === 'M'
            ? 'r:mythic'
            : ''
        )
        .filter(Boolean);
      if (rarityTerms.length > 0) {
        filters.push(rarityTerms.length > 1 ? `(${rarityTerms.join(' or ')})` : rarityTerms[0]);
      }
    }

    return { filters, formatKey };
  };

  const scryfallPreview = (() => {
    const { filters, formatKey } = buildScryfallFilters();
    const parts = [searchQuery.trim(), ...filters, formatKey ? `f:${formatKey}` : ''].filter(Boolean);
    return parts.join(' ');
  })();

  const resetAllAdvanced = () => {
    setColorFilter([]);
    setColorMatchMode('identity');
    setRarityFilter([]);
    setTypeFilter('');
    setOracleFilter('');
    setMvOperator('=');
    setCmcFilter('');
    setFormatFilter('');
    setAdvResults(null);
    setError(null);
  };

  const toggleQuickWord = (current: string, setter: (v: string) => void, word: string) => {
    const tokens = current
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    const exists = tokens.some((t) => t.toLowerCase() === word.toLowerCase());
    if (exists) {
      setter(tokens.filter((t) => t.toLowerCase() !== word.toLowerCase()).join(' '));
    } else {
      setter([...tokens, word].join(' '));
    }
  };

  const hasCustomTuning = Boolean(
    toolbar &&
      (toolbar.mvParity !== 'all' ||
        toolbar.excludeZero ||
        toolbar.activePlan !== 'base')
  );

  const toggleColor = (color: string) => {
    setColorFilter(prev =>
      prev.includes(color)
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  const toggleRarity = (rarity: string) => {
    setRarityFilter(prev =>
      prev.includes(rarity)
        ? prev.filter(r => r !== rarity)
        : [...prev, rarity]
    );
  };

  const buildDeckCardFromScryfall = (data: ScryfallCard, qty: number): Card => {
    const rarityMap: Record<string, 'C' | 'U' | 'R' | 'M' | 'B'> = {
      common: 'C',
      uncommon: 'U',
      rare: 'R',
      mythic: 'M',
      special: 'R',
      bonus: 'R',
    };

    const oracleText = data.oracle_text?.toLowerCase() || '';
    const mentionsTapped =
      oracleText.includes('enters the battlefield tapped') || oracleText.includes('enters tapped');
    const isLandType = data.type_line?.toLowerCase().includes('land');
    const entersTapped = Boolean(isLandType && mentionsTapped);

    let tapCondition: 'always' | 'conditional' | undefined;
    if (entersTapped) {
      const hasUnless = oracleText.includes('unless');
      const hasPay = oracleText.includes('pay') && oracleText.includes('life');
      const hasIf = oracleText.includes('if you control');
      const hasReveal = oracleText.includes('reveal') && oracleText.includes('from your hand');
      tapCondition = hasUnless || hasPay || hasIf || hasReveal ? 'conditional' : 'always';
    }

    const frontFaceTypeLine = data.card_faces?.[0]?.type_line ?? data.type_line ?? '';
    const fullTypeLine = data.type_line || '';
    let typeToCheck = frontFaceTypeLine;
    if (fullTypeLine.includes('//')) {
      typeToCheck = fullTypeLine.split('//')[0].trim();
    }

    const isLand = typeToCheck.toLowerCase().includes('land');
    const isBasicLand = typeToCheck.toLowerCase().includes('basic land');

    const keywords = data.keywords || [];
    const hasPartner = keywords.includes('Partner') && !oracleText.includes('partner with');

    let hasPartnerWith: string | undefined;
    const partnerWithMatch = oracleText.match(/partner with ([^(]+)/i);
    if (partnerWithMatch) {
      hasPartnerWith = partnerWithMatch[1].trim();
    }

    const isBackground = fullTypeLine.toLowerCase().includes('background');
    const choosesBackground =
      keywords.includes('Choose a Background') || oracleText.includes('choose a background');

    const isCompanionType = keywords.includes('Companion');
    let companionCondition: string | undefined;
    if (isCompanionType) {
      const companionMatch = oracleText.match(/companion\s*—\s*([^(]+)/i);
      if (companionMatch) {
        companionCondition = companionMatch[1].trim();
      }
    }

    const typeLineLower = fullTypeLine.toLowerCase();
    let isExtraCard = false;
    let extraCardType: 'plane' | 'phenomenon' | 'scheme' | 'vanguard' | 'conspiracy' | 'other' | undefined;
    if (typeLineLower.includes('plane ') || typeLineLower.includes('plane —')) {
      isExtraCard = true;
      extraCardType = 'plane';
    } else if (typeLineLower.includes('phenomenon')) {
      isExtraCard = true;
      extraCardType = 'phenomenon';
    } else if (typeLineLower.includes('scheme')) {
      isExtraCard = true;
      extraCardType = 'scheme';
    } else if (typeLineLower.includes('vanguard')) {
      isExtraCard = true;
      extraCardType = 'vanguard';
    } else if (typeLineLower.includes('conspiracy')) {
      isExtraCard = true;
      extraCardType = 'conspiracy';
    }

    const hasTwoFaces = !!(data.card_faces && data.card_faces.length > 1);
    const isDoubleFaced =
      hasTwoFaces &&
      (data.layout === 'transform' ||
        data.layout === 'modal_dfc' ||
        data.layout === 'double_faced_token' ||
        data.layout === 'flip' ||
        data.layout === 'reversible_card' ||
        !!data.card_faces?.[1]?.image_uris);
    const backImageUrl = isDoubleFaced
      ? data.card_faces?.[1]?.image_uris?.normal ||
        (data.card_faces?.[1]
          ? `https://cards.scryfall.io/normal/back/${data.id.slice(0, 1)}/${data.id.slice(1, 2)}/${data.id}.jpg`
          : undefined)
      : undefined;
    const backName = isDoubleFaced ? data.card_faces?.[1]?.name : undefined;
    const backType = isDoubleFaced ? data.card_faces?.[1]?.type_line : undefined;
    const backCmc = isDoubleFaced ? data.card_faces?.[1]?.cmc ?? 0 : undefined;

    return {
      id: `${data.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: data.name,
      quantity: qty,
      type: fullTypeLine,
      manaCost:
        data.layout === 'adventure' || data.layout === 'split'
          ? data.card_faces?.[0]?.mana_cost || data.mana_cost || ''
          : data.mana_cost || data.card_faces?.[0]?.mana_cost || '',
      cmc: data.cmc,
      colors: data.colors || data.card_faces?.[0]?.colors || [],
      colorIdentity: data.color_identity || [],
      imageUrl: getCardImageUrl(data),
      scryfallId: data.id,
      isLand,
      rarity: isBasicLand ? 'B' : rarityMap[data.rarity] || 'C',
      entersTapped,
      tapCondition,
      isDoubleFaced,
      layout: data.layout,
      backImageUrl,
      backName,
      backType,
      backCmc,
      hasPartner,
      hasPartnerWith,
      isBackground,
      choosesBackground,
      isCompanion: isCompanionType,
      companionCondition,
      isExtraCard,
      extraCardType,
      oracleText: data.oracle_text || data.card_faces?.[0]?.oracle_text || '',
      legalities: data.legalities,
    };
  };

  const handleBrowseCards = async () => {
    const { filters, formatKey } = buildScryfallFilters();
    const name = searchQuery.trim();
    const fullQuery = [name, ...filters, formatKey ? `f:${formatKey}` : ''].filter(Boolean).join(' ');
    if (!fullQuery) {
      setError('Select at least one filter or type a keyword to browse cards.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setIsBrowsing(true);
    setError(null);
    try {
      const results = await searchCards(fullQuery, sortOrder);
      setAdvResults(results.slice(0, 48));
      if (results.length === 0) {
        setError(`No cards matched "${fullQuery}"`);
        setTimeout(() => setError(null), 3500);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to browse Scryfall cards';
      setError(message);
      setTimeout(() => setError(null), 3500);
    } finally {
      setIsBrowsing(false);
    }
  };

  const handleAddScryfallResult = (data: ScryfallCard, toSideboard: boolean) => {
    const card = buildDeckCardFromScryfall(data, quantity);
    if (toSideboard && onAddToSideboard) {
      onAddToSideboard(card);
    } else {
      onAddCard(card);
    }
    const feedbackKey = `${data.id}-${toSideboard ? 'sb' : 'mb'}`;
    setAddedResultKey(feedbackKey);
    setTimeout(() => {
      setAddedResultKey((prev) => (prev === feedbackKey ? null : prev));
    }, 1100);
  };

  const handleAdd = async (toSideboard: boolean = false) => {
    const name = searchQuery.trim();
    if (!name && activeFilterCount === 0) return;

    // When no specific card name is typed, open the Advanced Card Finder and browse matching cards
    if (!name && activeFilterCount > 0) {
      setShowAdvanced(true);
      await handleBrowseCards();
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { filters, formatKey } = buildScryfallFilters();

      let data: ScryfallCard;
      if (filters.length === 0) {
        data = await fetchCardData(name, 'fuzzy');
        if (formatKey && data.legalities) {
          const legality = data.legalities[formatKey];
          if (legality && legality !== 'legal' && legality !== 'restricted') {
            throw new Error(
              `"${data.name}" is not legal in ${format === 'Auto' ? formatKey : formatFilter || format}`
            );
          }
        }
      } else {
        const fullQuery = [name, ...filters, formatKey ? `f:${formatKey}` : ''].filter(Boolean).join(' ');
        const results = await searchCards(fullQuery, sortOrder);
        if (results.length === 0) {
          throw new Error(`No cards match "${fullQuery}"`);
        }
        const lowerName = name.toLowerCase();
        data = results.find((c) => c.name.toLowerCase() === lowerName) ?? results[0];
      }

      const card = buildDeckCardFromScryfall(data, quantity);
      if (toSideboard && onAddToSideboard) {
        onAddToSideboard(card);
      } else {
        onAddCard(card);
      }
      setSearchQuery('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to search card';
      setError(message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleAdd(false);
  };

  const matchLabel = toolbar?.activePlan === 'base'
    ? 'Base'
    : `M${toolbar?.activePlan === 'A' ? '1' : toolbar?.activePlan === 'B' ? '2' : toolbar?.activePlan === 'C' ? '3' : toolbar?.activePlan === 'D' ? '4' : '5'}`;

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      {/* TOP TOOLBAR: 2 Centered Rows */}
      <div className="bg-slate-900/70 border border-white/10 rounded-xl px-2.5 py-2 shadow-lg space-y-2">
        {/* ROW 1 (Centered): Format, Import, Search + +MAIN / +SIDE, ending with Advanced Settings Cog at far right */}
        <div className="flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap">
        {/* Format Selector */}
        {toolbar && (
          <BrewTooltip
            title="Deck Format"
            badge={format || 'Standard'}
            description="Sets legality validation for searched cards and defaults your target deck size (60 for Constructed, 99 for Commander)."
            accent="purple"
          >
            <select
              value={format || 'Standard'}
              onChange={(e) => onFormatChange?.(e.target.value)}
              className="bg-purple-950/80 border border-purple-500/50 hover:border-purple-400 text-amber-300 rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-purple-400 cursor-pointer"
            >
              <option value="Standard">Standard</option>
              <option value="Pioneer">Pioneer</option>
              <option value="Modern">Modern</option>
              <option value="Legacy">Legacy</option>
              <option value="Vintage">Vintage</option>
              <option value="Commander">Commander</option>
              <option value="Pauper">Pauper</option>
            </select>
          </BrewTooltip>
        )}

        {/* Import Button */}
        {onShowImport && (
          <BrewTooltip
            title="Import Decklist"
            badge="MTGA / Text"
            description="Paste or upload a full decklist and sideboard from MTG Arena, Moxfield, or plain text."
            accent="amber"
          >
            <button
              type="button"
              onClick={onShowImport}
              className="px-2.5 sm:px-3 py-1.5 bg-gradient-to-r from-purple-600 to-amber-500 text-white border border-purple-400/50 hover:from-purple-500 hover:to-amber-400 rounded-lg text-xs font-semibold shadow-sm transition-all"
            >
              Import
            </button>
          </BrewTooltip>
        )}

        {/* Search Input + +MAIN / +SIDE Buttons + Far-Right Advanced Settings Cog */}
        <div className="flex items-center gap-1 flex-1 min-w-[240px] max-w-[440px]">
          <BrewTooltip
            title="Card Search"
            badge="Fuzzy Match"
            description="Type any Magic card name and press Enter (or click +MAIN / +SIDE) to add it to your deck."
            detail="Tip: Click any card in your deck to customize its MV, ramp/draw tags, or Commander status."
            accent="purple"
            className="relative flex-1 min-w-[110px]"
          >
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/40 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search card name..."
              className="w-full bg-white/5 border border-white/15 rounded-lg pl-8 pr-2 py-1.5 text-white text-xs placeholder:text-white/40 focus:outline-none focus:border-purple-500/60 focus:bg-white/10 transition-colors"
              disabled={isLoading}
            />
          </BrewTooltip>

          <BrewTooltip
            title="Add to Main Deck (+MAIN)"
            badge={quantity > 1 ? `${quantity}× • Enter ↵` : 'Enter ↵'}
            description={`Searches Scryfall and adds ${quantity} cop${quantity === 1 ? 'y' : 'ies'} of this card to your Main Deck.`}
            accent="purple"
          >
            <button
              type="submit"
              disabled={isLoading || (!searchQuery.trim() && activeFilterCount === 0)}
              className="px-2.5 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-white/10 disabled:text-white/30 text-white rounded-lg text-xs font-semibold border border-purple-400/40 disabled:border-white/5 transition-colors whitespace-nowrap"
            >
              +MAIN
            </button>
          </BrewTooltip>

          {onAddToSideboard && (
            <BrewTooltip
              title="Add to Sideboard (+SIDE)"
              badge={quantity > 1 ? `${quantity}×` : undefined}
              description={`Adds ${quantity} cop${quantity === 1 ? 'y' : 'ies'} directly to your Sideboard (or Command / Companion zone).`}
              accent="amber"
            >
              <button
                type="button"
                onClick={() => handleAdd(true)}
                disabled={isLoading || (!searchQuery.trim() && activeFilterCount === 0)}
                className="px-2.5 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:bg-white/10 disabled:text-white/30 text-white rounded-lg text-xs font-semibold border border-amber-400/40 disabled:border-white/5 transition-colors whitespace-nowrap"
              >
                +SIDE
              </button>
            </BrewTooltip>
          )}

          {/* Far-Right Advanced Card Finder Cog */}
          <BrewTooltip
            title="Advanced Card Finder"
            badge={activeFilterCount > 0 ? `${activeFilterCount} Active` : showAdvanced ? 'Open' : 'Filters'}
            description="Open the Scryfall Card Finder to filter and browse cards by Color Identity, Type, Mana Value, Oracle Rules, and Rarity."
            accent="amber"
          >
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`p-1.5 rounded-lg border transition-all relative flex-shrink-0 ${
                showAdvanced || activeFilterCount > 0
                  ? 'bg-purple-500/25 border-purple-500/60 text-amber-400 shadow-[0_0_10px_rgba(168,85,247,0.25)]'
                  : 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              <Settings className={`size-4 transition-transform duration-200 ${showAdvanced ? 'rotate-45' : ''}`} />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-500 text-[#020617] text-[8px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </BrewTooltip>
        </div>

        </div>

        {/* ROW 2 (Centered): Starting with View Options (Curve/List/Grid), Match Plan, Tune Drawer, Deck Actions */}
        {toolbar && (
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap pt-1.5 border-t border-white/5">
            {/* Explicit Segmented View Switcher */}
            <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-lg p-0.5">
              <BrewTooltip
                title="Curve View (Dynamic MVD Grid)"
                badge="Recommended"
                description="Interactive Mana Value columns showing your ideal curve slot targets, castability odds, and per-copy mana overrides."
                accent="purple"
              >
                <button
                  type="button"
                  onClick={() => toolbar.setViewMode('dynamic')}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                    toolbar.viewMode === 'dynamic'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Zap className="size-3 text-amber-400" />
                  <span>Curve</span>
                </button>
              </BrewTooltip>

              <BrewTooltip
                title="Compact List View"
                description="Dense table view grouped by Type, Mana Value, Color, or Name with inline quantities, weights, and matchup toggles."
                accent="cyan"
              >
                <button
                  type="button"
                  onClick={() => toolbar.setViewMode('list')}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                    toolbar.viewMode === 'list'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <List className="size-3" />
                  <span>List</span>
                </button>
              </BrewTooltip>

              <BrewTooltip
                title="Visual Card Grid"
                description="Full card-art gallery grouped by category with hover zoom and quick quantity controls."
                accent="amber"
              >
                <button
                  type="button"
                  onClick={() => toolbar.setViewMode('grid')}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                    toolbar.viewMode === 'grid'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <LayoutGrid className="size-3" />
                  <span>Grid</span>
                </button>
              </BrewTooltip>
            </div>

            {/* Matchup Plan Quick Selector */}
            <BrewTooltip
              title="Matchup Plan (Base / M1–M5)"
              badge={matchLabel}
              description="Switch between your Base maindeck and 5 post-sideboard Matchup Plans (M1–M5). In M1–M5, you can swap sideboard cards in/out and override card mana costs."
              accent="cyan"
            >
              <select
                value={toolbar.activePlan}
                onChange={(e) => toolbar.setActivePlan(e.target.value)}
                className={`border rounded-lg px-2 py-1.5 text-xs font-semibold cursor-pointer focus:outline-none transition-colors ${
                  toolbar.activePlan !== 'base'
                    ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                    : 'bg-white/5 border-white/10 text-white/80 hover:border-white/20'
                }`}
              >
                <option value="base" className="bg-slate-900 text-white">Plan: Base</option>
                <option value="A" className="bg-slate-900 text-cyan-300">Plan: Match 1</option>
                <option value="B" className="bg-slate-900 text-cyan-300">Plan: Match 2</option>
                <option value="C" className="bg-slate-900 text-cyan-300">Plan: Match 3</option>
                <option value="D" className="bg-slate-900 text-cyan-300">Plan: Match 4</option>
                <option value="E" className="bg-slate-900 text-cyan-300">Plan: Match 5</option>
              </select>
            </BrewTooltip>

            {/* Tune Drawer Toggle Button */}
            <BrewTooltip
              title="Curve & Target Tuning"
              badge={showTuning ? 'Open' : 'Targets & Math'}
              description="Open the tuning bar to customize Target Deck Size, Target Lands, Weight-Per-Land Ratio, Companion Parity (Odds/Evens), 0-MV slots, Auto-Lands, and Play/Draw evaluation turn."
              accent="amber"
            >
              <button
                type="button"
                onClick={() => setShowTuning(!showTuning)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-all relative ${
                  showTuning
                    ? 'bg-amber-500/25 border-amber-500/60 text-amber-300 shadow-sm'
                    : 'bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <SlidersHorizontal className="size-3.5 text-amber-400" />
                <span>Tune</span>
                {hasCustomTuning && !showTuning && (
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                )}
              </button>
            </BrewTooltip>

            {onToggleAutoSuggestLands && (
              <BrewTooltip
                title="Auto-Suggest Basic Lands"
                badge={autoSuggestLands ? 'Active' : 'Off'}
                description="Automatically fills or rebalances basic lands (Plains, Island, Swamp, Mountain, Forest) to match your spells' color pip demands up to your Target Land count."
                accent="cyan"
              >
                <button
                  type="button"
                  onClick={() => onToggleAutoSuggestLands(!autoSuggestLands)}
                  className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-all whitespace-nowrap ${
                    autoSuggestLands
                      ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/50 shadow-[0_0_8px_rgba(6,182,212,0.35)]'
                      : 'bg-white/5 text-white/70 border-white/10 hover:text-white hover:bg-white/10'
                  }`}
                >
                  Auto Lands
                </button>
              </BrewTooltip>
            )}

            <span className="hidden sm:inline-block w-px h-5 bg-white/10" />

            {/* Deck Action Icons with Rich Tooltips */}
            <div className="flex items-center gap-0.5">
              <BrewTooltip
                title="Save Deck"
                description="Save this decklist, format, cover card, and matchup plans to your DECKSTR profile."
                accent="green"
              >
                <button
                  type="button"
                  onClick={toolbar.saveDeck}
                  className="p-1.5 hover:bg-green-500/20 text-white/50 hover:text-green-400 rounded-lg transition-colors"
                >
                  <Save className="size-4" />
                </button>
              </BrewTooltip>

              <BrewTooltip
                title={copiedFeedback ? 'Copied to Clipboard!' : 'Copy Decklist'}
                badge="Arena / Text"
                description="Copy your currently viewed Main Deck and Sideboard to the clipboard in standard MTG Arena export format."
                accent="cyan"
              >
                <button
                  type="button"
                  onClick={() => {
                    toolbar.copyToClipboard();
                    setCopiedFeedback(true);
                    setTimeout(() => setCopiedFeedback(false), 1800);
                  }}
                  className="p-1.5 hover:bg-cyan-500/20 text-white/50 hover:text-cyan-400 rounded-lg transition-colors"
                >
                  {copiedFeedback ? <Check className="size-4 text-green-400" /> : <Copy className="size-4" />}
                </button>
              </BrewTooltip>

              <BrewTooltip
                title="Reset Matchup Plans"
                description="Clear all custom M1–M5 sideboard swaps and per-copy mana value overrides back to the Base deck."
                accent="amber"
              >
                <button
                  type="button"
                  onClick={toolbar.clearPlan}
                  className="p-1.5 hover:bg-amber-500/20 text-white/50 hover:text-amber-400 rounded-lg transition-colors"
                >
                  <RotateCcw className="size-4" />
                </button>
              </BrewTooltip>

              <BrewTooltip
                title="Clear Entire Deck"
                badge="Caution"
                description="Remove all cards from both Main Deck and Sideboard to start a fresh brew."
                accent="red"
              >
                <button
                  type="button"
                  onClick={toolbar.clearDeck}
                  className="p-1.5 hover:bg-red-500/20 text-white/50 hover:text-red-400 rounded-lg transition-colors"
                >
                  <Trash2 className="size-4" />
                </button>
              </BrewTooltip>
            </div>
          </div>
        )}
      </div>

      {/* UPGRADED ADVANCED CARD FINDER DRAWER */}
      {showAdvanced && (
        <div
          className="bg-slate-950/95 border border-purple-500/35 rounded-xl p-3 sm:p-3.5 shadow-2xl space-y-3 animate-in fade-in duration-150"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault();
              handleBrowseCards();
            }
          }}
        >
          {/* Drawer Header: Title + Live Scryfall Query Pill + Add Qty + Reset/Close */}
          <div className="flex items-center justify-between gap-2 flex-wrap pb-2 border-b border-white/10">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="text-xs font-bold text-white flex items-center gap-1.5">
                <Settings className="size-3.5 text-amber-400" />
                <span>Advanced Card Finder</span>
              </span>
              {scryfallPreview && (
                <BrewTooltip
                  title="Live Scryfall Query"
                  badge="Syntax Preview"
                  description="Real-time Scryfall search syntax built from your active filters below."
                  detail={scryfallPreview}
                  accent="purple"
                >
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-purple-500/15 border border-purple-500/30 text-purple-200 truncate max-w-[260px] sm:max-w-[380px] cursor-help">
                    {scryfallPreview}
                  </span>
                </BrewTooltip>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              {/* Quick Add Quantity Selector */}
              <BrewTooltip
                title="Add Copy Quantity"
                badge={`${quantity}× per add`}
                description={`Sets how many copies are added each time you click +MAIN or +SIDE (${quantity} cop${quantity === 1 ? 'y' : 'ies'}).`}
                detail="1×: Singleton / Commander | 4×: Full Playset"
                accent="purple"
              >
                <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-lg p-0.5">
                  <span className="text-[9px] uppercase font-bold text-white/40 px-1.5 select-none">Qty</span>
                  {[1, 2, 3, 4].map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setQuantity(q)}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold transition-colors ${
                        quantity === q
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'text-white/50 hover:text-white'
                      }`}
                    >
                      {q}×
                    </button>
                  ))}
                </div>
              </BrewTooltip>

              {(activeFilterCount > 0 || advResults !== null) && (
                <BrewTooltip
                  title="Reset Card Finder"
                  badge={activeFilterCount > 0 ? `${activeFilterCount} Active` : 'Clear'}
                  description="Clear all active color, rarity, type, mana value, oracle text, and legality filters and close the search results."
                  accent="amber"
                >
                  <button
                    type="button"
                    onClick={resetAllAdvanced}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 hover:bg-amber-500/20 border border-white/10 hover:border-amber-400/40 text-[10px] font-semibold text-amber-300 transition-colors"
                  >
                    <RotateCcw className="size-3" />
                    <span>Reset</span>
                  </button>
                </BrewTooltip>
              )}

              <BrewTooltip
                title="Close Advanced Card Finder"
                description="Collapse the Advanced Card Finder panel. Active filters stay applied until reset."
                accent="slate"
              >
                <button
                  type="button"
                  onClick={() => setShowAdvanced(false)}
                  className="p-1 rounded-lg bg-white/5 hover:bg-white/15 border border-white/10 text-white/50 hover:text-white transition-colors"
                >
                  <X className="size-3.5" />
                </button>
              </BrewTooltip>
            </div>
          </div>

          {/* Main 2-Column Filter Workspace */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            {/* LEFT COLUMN (5 cols): Colors, Color Match Mode & Rarity */}
            <div className="lg:col-span-5 space-y-3 bg-white/[0.025] border border-white/10 rounded-xl p-2.5">
              {/* Color Filter + Mode */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-1.5 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300/90">
                    Colors & Identity
                  </span>

                  <div className="flex items-center gap-1">
                    {deckColors.length > 0 && (
                      <BrewTooltip
                        title="Match Current Deck Colors"
                        badge={deckColors.join('')}
                        description="Automatically select the mana colors already present in your current decklist."
                        accent="purple"
                      >
                        <button
                          type="button"
                          onClick={() => setColorFilter(deckColors)}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-500/20 hover:bg-purple-500/35 border border-purple-400/35 text-[9px] font-semibold text-purple-200 transition-colors"
                        >
                          <Sparkles className="size-2.5 text-amber-300" />
                          <span>Match Deck</span>
                        </button>
                      </BrewTooltip>
                    )}

                    <div className="inline-flex items-center bg-black/40 border border-white/10 rounded-md p-0.5 text-[9px] font-semibold">
                      {(
                        [
                          {
                            id: 'identity',
                            label: 'Id ≤',
                            title: 'Commander Color Identity (id≤)',
                            badge: 'Commander Legal',
                            desc: 'Finds cards whose total color identity fits within your selected colors (includes colorless cards and subsets).',
                            accent: 'purple' as const,
                          },
                          {
                            id: 'includes',
                            label: 'Has ≥',
                            title: 'Includes Colors (c≥)',
                            badge: 'At Least',
                            desc: 'Finds cards that contain all of your selected colors (and may also include other colors).',
                            accent: 'cyan' as const,
                          },
                          {
                            id: 'exact',
                            label: 'Exact =',
                            title: 'Exact Color Match (c=)',
                            badge: 'Strict Match',
                            desc: 'Finds cards whose colors match your selected mana symbols exclusively, with no extra or missing colors.',
                            accent: 'amber' as const,
                          },
                        ] as const
                      ).map((mode) => (
                        <BrewTooltip
                          key={mode.id}
                          title={mode.title}
                          badge={mode.badge}
                          description={mode.desc}
                          accent={mode.accent}
                        >
                          <button
                            type="button"
                            onClick={() => setColorMatchMode(mode.id)}
                            className={`px-1.5 py-0.5 rounded transition-colors ${
                              colorMatchMode === mode.id
                                ? 'bg-purple-600 text-white'
                                : 'text-white/45 hover:text-white/80'
                            }`}
                          >
                            {mode.label}
                          </button>
                        </BrewTooltip>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Mana Orbs */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[
                    { code: 'W', name: 'White' },
                    { code: 'U', name: 'Blue' },
                    { code: 'B', name: 'Black' },
                    { code: 'R', name: 'Red' },
                    { code: 'G', name: 'Green' },
                    { code: 'C', name: 'Colorless' },
                  ].map(({ code, name }) => {
                    const active = colorFilter.includes(code);
                    return (
                      <BrewTooltip
                        key={code}
                        title={`${name} Mana Filter (${code})`}
                        badge={active ? 'Selected' : 'Click to toggle'}
                        description={
                          code === 'C'
                            ? 'Filter for strictly Colorless spells, artifacts, Eldrazi, and utility lands.'
                            : `Include ${name} (${code}) in your active ${
                                colorMatchMode === 'identity'
                                  ? 'Color Identity (id≤)'
                                  : colorMatchMode === 'includes'
                                  ? 'Includes-Color (c≥)'
                                  : 'Exact-Color (c=)'
                              } filter.`
                        }
                        accent={active ? 'amber' : 'purple'}
                      >
                        <button
                          type="button"
                          onClick={() => toggleColor(code)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[10px] font-semibold transition-all ${
                            active
                              ? 'bg-purple-500/25 border-amber-400/70 text-white shadow-[0_0_8px_rgba(251,191,36,0.2)]'
                              : 'bg-white/[0.04] border-white/10 text-white/55 hover:text-white hover:border-white/25'
                          }`}
                        >
                          <ManaSymbol symbol={code} size="sm" />
                          <span className="hidden sm:inline">{code}</span>
                        </button>
                      </BrewTooltip>
                    );
                  })}
                  {colorFilter.length > 0 && (
                    <BrewTooltip
                      title="Clear Color Filter"
                      description="Deselect all chosen mana colors."
                      accent="slate"
                    >
                      <button
                        type="button"
                        onClick={() => setColorFilter([])}
                        className="text-[10px] text-white/45 hover:text-white px-1.5 py-1"
                      >
                        Clear
                      </button>
                    </BrewTooltip>
                  )}
                </div>
              </div>

              {/* Rarity Crest Buttons (Common = White, Uncommon = Silver, Rare = Gold, Mythic = Orange) */}
              <div className="pt-2 border-t border-white/10 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300/90">
                    Rarity
                  </span>
                  {rarityFilter.length > 0 && (
                    <BrewTooltip
                      title="Clear Rarity Filter"
                      description="Remove all active rarity restrictions."
                      accent="slate"
                    >
                      <button
                        type="button"
                        onClick={() => setRarityFilter([])}
                        className="text-[10px] text-white/45 hover:text-white"
                      >
                        Clear
                      </button>
                    </BrewTooltip>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    {
                      key: 'C',
                      label: 'Common',
                      icon: '/rarity/common.png',
                      badge: 'r:common',
                      desc: 'Filter for Common printings — foundational role-players and Pauper-legal staples.',
                      accent: 'slate' as const,
                      activeCls:
                        'bg-white/15 border-white/70 text-white shadow-[0_0_12px_rgba(255,255,255,0.22)]',
                    },
                    {
                      key: 'U',
                      label: 'Uncommon',
                      icon: '/rarity/uncommon.png',
                      badge: 'r:uncommon',
                      desc: 'Filter for Uncommon printings — synergistic build-arounds and efficient interaction.',
                      accent: 'cyan' as const,
                      activeCls:
                        'bg-slate-400/20 border-slate-300/75 text-slate-100 shadow-[0_0_12px_rgba(203,213,225,0.25)]',
                    },
                    {
                      key: 'R',
                      label: 'Rare',
                      icon: '/rarity/rare.png',
                      badge: 'r:rare',
                      desc: 'Filter for Rare printings — format staples, dual lands, and high-impact engines.',
                      accent: 'amber' as const,
                      activeCls:
                        'bg-amber-500/20 border-amber-400/80 text-amber-100 shadow-[0_0_12px_rgba(251,191,36,0.3)]',
                    },
                    {
                      key: 'M',
                      label: 'Mythic',
                      icon: '/rarity/mythic.png',
                      badge: 'r:mythic',
                      desc: 'Filter for Mythic Rare printings — marquee finishers, planeswalkers, and legendary threats.',
                      accent: 'amber' as const,
                      activeCls:
                        'bg-orange-500/25 border-orange-400/85 text-orange-100 shadow-[0_0_12px_rgba(249,115,22,0.35)]',
                    },
                  ].map(({ key, label, icon, badge, desc, accent, activeCls }) => {
                    const active = rarityFilter.includes(key);
                    return (
                      <BrewTooltip
                        key={key}
                        title={`${label} Rarity`}
                        badge={active ? 'Selected' : badge}
                        description={desc}
                        accent={accent}
                        className="flex w-full"
                      >
                        <button
                          type="button"
                          onClick={() => toggleRarity(key)}
                          className={`w-full group flex flex-col items-center justify-center gap-1 py-1.5 px-1 rounded-xl border text-[10px] font-semibold transition-all ${
                            active
                              ? activeCls
                              : 'bg-white/[0.04] border-white/10 text-white/55 hover:text-white hover:bg-white/[0.07] hover:border-white/25'
                          }`}
                        >
                          <img
                            src={icon}
                            alt={label}
                            className={`w-7 h-7 object-contain transition-transform duration-150 ${
                              active
                                ? 'scale-110 drop-shadow-[0_2px_6px_rgba(0,0,0,0.65)]'
                                : 'opacity-80 group-hover:opacity-100 group-hover:scale-105'
                            }`}
                          />
                          <span className="leading-none">{label}</span>
                        </button>
                      </BrewTooltip>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN (7 cols): Type Line, Mana Value, Oracle Rules & Format/Sort */}
            <div className="lg:col-span-7 space-y-2.5 bg-white/[0.025] border border-white/10 rounded-xl p-2.5">
              {/* Row A: Card Type + Mana Value */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
                {/* Type Line + Quick Type Chips */}
                <div className="sm:col-span-7 space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-cyan-300/90 block">
                    Card Type / Subtype
                  </label>
                  <BrewTooltip
                    title="Card Type & Subtype Filter"
                    badge="Scryfall t:"
                    description="Enter one or more supertypes, card types, or subtypes separated by spaces. Every word entered must appear on the card's type line."
                    detail="Examples: Creature Elf | Legendary Vampire | Artifact Equipment"
                    accent="cyan"
                    className="block w-full"
                  >
                    <input
                      type="text"
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      placeholder="e.g. Creature Elf, Artifact, Equipment..."
                      className="w-full bg-black/40 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/60"
                    />
                  </BrewTooltip>
                  <div className="flex items-center gap-1 flex-wrap">
                    {['Creature', 'Instant', 'Sorcery', 'Artifact', 'Enchantment', 'Planeswalker', 'Land', 'Legendary'].map(
                      (t) => {
                        const isPicked = typeFilter
                          .split(/\s+/)
                          .some((tok) => tok.toLowerCase() === t.toLowerCase());
                        return (
                          <BrewTooltip
                            key={t}
                            title={`${t} Type Filter`}
                            badge={isPicked ? 'Active' : `t:${t.toLowerCase()}`}
                            description={`Click to ${isPicked ? 'remove' : 'require'} "${t}" on the card's type line.`}
                            accent="cyan"
                          >
                            <button
                              type="button"
                              onClick={() => toggleQuickWord(typeFilter, setTypeFilter, t)}
                              className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border transition-colors ${
                                isPicked
                                  ? 'bg-cyan-500/25 border-cyan-400/60 text-cyan-200'
                                  : 'bg-white/[0.04] border-white/10 text-white/50 hover:text-white/85'
                              }`}
                            >
                              {t}
                            </button>
                          </BrewTooltip>
                        );
                      }
                    )}
                  </div>
                </div>

                {/* Mana Value + Operator + Quick MV Pills */}
                <div className="sm:col-span-5 space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-amber-300/90 block">
                    Mana Value (MV)
                  </label>
                  <div className="flex items-center gap-1">
                    <div className="inline-flex items-center bg-black/40 border border-white/15 rounded-lg p-0.5 text-[10px] font-mono font-bold">
                      {(
                        [
                          {
                            op: '=',
                            sym: '=',
                            title: 'Exact Mana Value (mv=)',
                            badge: 'Equals',
                            desc: 'Match cards whose Mana Value equals the target number.',
                          },
                          {
                            op: '<=',
                            sym: '≤',
                            title: 'At Most Mana Value (mv≤)',
                            badge: 'Or Less',
                            desc: 'Match cards whose Mana Value is less than or equal to the target number.',
                          },
                          {
                            op: '>=',
                            sym: '≥',
                            title: 'At Least Mana Value (mv≥)',
                            badge: 'Or Greater',
                            desc: 'Match cards whose Mana Value is greater than or equal to the target number.',
                          },
                        ] as const
                      ).map((item) => (
                        <BrewTooltip
                          key={item.op}
                          title={item.title}
                          badge={item.badge}
                          description={item.desc}
                          accent="amber"
                        >
                          <button
                            type="button"
                            onClick={() => setMvOperator(item.op)}
                            className={`px-1.5 py-1 rounded transition-colors ${
                              mvOperator === item.op
                                ? 'bg-amber-500/30 text-amber-200'
                                : 'text-white/45 hover:text-white'
                            }`}
                          >
                            {item.sym}
                          </button>
                        </BrewTooltip>
                      ))}
                    </div>
                    <BrewTooltip
                      title="Mana Value Target"
                      badge={cmcFilter ? `MV ${mvOperator} ${cmcFilter}` : 'Any MV'}
                      description="Enter a Mana Value number (e.g. 2) or range suffix (e.g. 7+) to target a specific curve slot."
                      accent="amber"
                      className="flex-1 min-w-0"
                    >
                      <input
                        type="text"
                        value={cmcFilter}
                        onChange={(e) => setCmcFilter(e.target.value)}
                        placeholder="Any"
                        className="w-full bg-black/40 border border-white/15 rounded-lg px-2 py-1.5 text-xs font-mono text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/60 text-center"
                      />
                    </BrewTooltip>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {['0', '1', '2', '3', '4', '5', '6', '7+'].map((mvChip) => {
                      const active = cmcFilter.trim() === mvChip;
                      return (
                        <BrewTooltip
                          key={mvChip}
                          title={mvChip === '7+' ? 'Filter 7+ Mana Value' : `Filter ${mvChip}-Drop Cards`}
                          badge={active ? 'Active' : mvChip === '7+' ? 'mv≥7' : `mv${mvOperator}${mvChip}`}
                          description={
                            mvChip === '7+'
                              ? 'Filter for 7+ mana finishers and top-end bombs.'
                              : `Filter for ${mvChip}-mana spells to fill your ${mvChip}-Drop curve column.`
                          }
                          accent="amber"
                        >
                          <button
                            type="button"
                            onClick={() => setCmcFilter(active ? '' : mvChip)}
                            className={`min-w-[22px] px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border transition-colors ${
                              active
                                ? 'bg-amber-500/30 border-amber-400/60 text-amber-200'
                                : 'bg-white/[0.04] border-white/10 text-white/50 hover:text-white'
                            }`}
                          >
                            {mvChip}
                          </button>
                        </BrewTooltip>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Row B: Oracle / Rules Text + Format & Sort */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 pt-2 border-t border-white/10">
                <div className="sm:col-span-7 space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-emerald-300/90 block">
                    Oracle / Rules Text
                  </label>
                  <BrewTooltip
                    title="Oracle Rules Text Filter"
                    badge="Scryfall o:"
                    description="Search exact phrases, abilities, or keywords inside official Oracle card text."
                    detail='Examples: "draw a card", "enters tapped", "flying", "proliferate"'
                    accent="green"
                    className="block w-full"
                  >
                    <input
                      type="text"
                      value={oracleFilter}
                      onChange={(e) => setOracleFilter(e.target.value)}
                      placeholder='e.g. draw a card, counter target, treasure...'
                      className="w-full bg-black/40 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-400/60"
                    />
                  </BrewTooltip>
                  <div className="flex items-center gap-1 flex-wrap">
                    {[
                      { label: 'Draw', text: 'draw a card' },
                      { label: 'Treasure', text: 'Treasure token' },
                      { label: 'Counter', text: 'counter target' },
                      { label: 'Destroy', text: 'destroy target' },
                      { label: 'Exile', text: 'exile target' },
                      { label: 'Tutor', text: 'search your library' },
                      { label: 'Lifegain', text: 'gain life' },
                      { label: 'Sacrifice', text: 'sacrifice a' },
                    ].map((chip) => {
                      const active = oracleFilter.trim().toLowerCase() === chip.text.toLowerCase();
                      return (
                        <BrewTooltip
                          key={chip.label}
                          title={`${chip.label} Oracle Filter`}
                          badge={active ? 'Active' : `o:"${chip.text}"`}
                          description={`Filter for cards whose Oracle rules text includes "${chip.text}".`}
                          accent="green"
                        >
                          <button
                            type="button"
                            onClick={() => setOracleFilter(active ? '' : chip.text)}
                            className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border transition-colors ${
                              active
                                ? 'bg-emerald-500/25 border-emerald-400/60 text-emerald-200'
                                : 'bg-white/[0.04] border-white/10 text-white/50 hover:text-white/85'
                            }`}
                          >
                            {chip.label}
                          </button>
                        </BrewTooltip>
                      );
                    })}
                  </div>
                </div>

                {/* Format Override + Sort By */}
                <div className="sm:col-span-5 grid grid-cols-2 gap-1.5 content-start">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-white/60 block">
                      Legality
                    </label>
                    <BrewTooltip
                      title="Format Legality Filter"
                      badge={formatFilter ? formatFilter.toUpperCase() : format || 'Standard'}
                      description="Restrict search results to cards legal in your deck's format, another constructed format, or Any Format."
                      accent="purple"
                      className="block w-full"
                    >
                      <select
                        value={formatFilter}
                        onChange={(e) => setFormatFilter(e.target.value)}
                        className="w-full bg-slate-900 border border-white/15 rounded-lg px-2 py-1.5 text-[11px] text-white focus:outline-none focus:border-purple-400/60"
                      >
                        <option value="">Deck ({format || 'Standard'})</option>
                        <option value="any">Any Format</option>
                        <option value="standard">Standard</option>
                        <option value="pioneer">Pioneer</option>
                        <option value="modern">Modern</option>
                        <option value="legacy">Legacy</option>
                        <option value="vintage">Vintage</option>
                        <option value="commander">Commander</option>
                        <option value="pauper">Pauper</option>
                      </select>
                    </BrewTooltip>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-white/60 block">
                      Sort Results
                    </label>
                    <BrewTooltip
                      title="Result Sort Order"
                      badge={
                        sortOrder === 'edhrec'
                          ? 'Popularity'
                          : sortOrder === 'cmc'
                          ? 'Mana Value'
                          : sortOrder === 'name'
                          ? 'A–Z'
                          : 'USD Price'
                      }
                      description="Order matching Scryfall cards by EDHREC/format popularity, ascending Mana Value, alphabetical name, or paper USD price."
                      accent="purple"
                      className="block w-full"
                    >
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as 'edhrec' | 'cmc' | 'name' | 'usd')}
                        className="w-full bg-slate-900 border border-white/15 rounded-lg px-2 py-1.5 text-[11px] text-white focus:outline-none focus:border-purple-400/60"
                      >
                        <option value="edhrec">Most Played</option>
                        <option value="cmc">Mana Value</option>
                        <option value="name">Name (A–Z)</option>
                        <option value="usd">Price ($)</option>
                      </select>
                    </BrewTooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Browse Action Bar + Inline Visual Card Results Grid */}
          <div className="pt-1 space-y-2.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <BrewTooltip
                title="Browse Matching Cards"
                badge="Scryfall Search"
                description="Fetch visual Scryfall matches right inside this panel so you can add cards directly to MAIN or SIDE with 1 click."
                detail="Shortcut: Press Enter inside any filter input"
                accent="purple"
              >
                <button
                  type="button"
                  disabled={isBrowsing || (!searchQuery.trim() && activeFilterCount === 0)}
                  onClick={handleBrowseCards}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 disabled:opacity-40 disabled:pointer-events-none text-white text-xs font-semibold border border-purple-400/40 shadow-md transition-all"
                >
                  {isBrowsing ? <Loader2 className="size-3.5 animate-spin" /> : <Search className="size-3.5" />}
                  <span>{isBrowsing ? 'Searching Scryfall...' : 'Browse Matching Cards'}</span>
                </button>
              </BrewTooltip>

              {advResults !== null && (
                <div className="flex items-center gap-2 text-[11px] text-white/60">
                  <span>
                    Showing <strong className="text-white">{advResults.length}</strong> card
                    {advResults.length === 1 ? '' : 's'}
                  </span>
                  <BrewTooltip
                    title="Collapse Results Grid"
                    description="Hide the Scryfall card grid while keeping your filter selections intact."
                    accent="slate"
                  >
                    <button
                      type="button"
                      onClick={() => setAdvResults(null)}
                      className="text-white/45 hover:text-white underline text-[10px]"
                    >
                      Hide results
                    </button>
                  </BrewTooltip>
                </div>
              )}
            </div>

            {advResults !== null && advResults.length > 0 && (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-[260px] overflow-y-auto pr-1"
                style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(168,85,247,0.35) transparent' }}
              >
                {advResults.map((sc) => {
                  const thumbUrl = getCardImageUrl(sc);
                  const costStr = sc.mana_cost || sc.card_faces?.[0]?.mana_cost || '';
                  const pips = (costStr.includes(' // ') ? costStr.split(' // ')[0] : costStr)
                    .split(/(?=[{])/g)
                    .filter(Boolean)
                    .map((pip) => pip.replace(/[{}]/g, '').replace('/', ''));
                  const mbKey = `${sc.id}-mb`;
                  const sbKey = `${sc.id}-sb`;

                  return (
                    <div
                      key={sc.id}
                      className="relative rounded-lg overflow-hidden border border-white/10 hover:border-purple-400/50 bg-slate-900/90 p-1.5 flex items-center gap-2 group transition-all"
                    >
                      {thumbUrl && (
                        <img
                          src={thumbUrl}
                          alt={sc.name}
                          loading="lazy"
                          className="w-9 h-12 rounded object-cover object-top flex-shrink-0 border border-white/15 bg-black/40"
                        />
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1">
                          <BrewTooltip
                            title={sc.name}
                            badge={`MV ${sc.cmc}`}
                            description={sc.oracle_text || sc.card_faces?.[0]?.oracle_text || sc.type_line}
                            detail={sc.type_line}
                            accent="purple"
                            className="min-w-0 truncate"
                          >
                            <span className="text-xs font-semibold text-white truncate cursor-help">
                              {sc.name}
                            </span>
                          </BrewTooltip>
                          {pips.length > 0 && (
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              {pips.slice(0, 5).map((sym, idx) => (
                                <ManaSymbol key={idx} symbol={sym} size="sm" />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-[10px] text-white/45 truncate">{sc.type_line}</div>

                        <div className="mt-1 flex items-center gap-1">
                          <BrewTooltip
                            title="Add to Main Deck (+MAIN)"
                            badge={`${quantity}×`}
                            description={`Add ${quantity} cop${quantity === 1 ? 'y' : 'ies'} of ${sc.name} to your Main Deck.`}
                            accent="purple"
                          >
                            <button
                              type="button"
                              onClick={() => handleAddScryfallResult(sc, false)}
                              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded bg-purple-600/80 hover:bg-purple-500 text-white text-[10px] font-semibold border border-purple-400/40 transition-colors"
                            >
                              {addedResultKey === mbKey ? (
                                <>
                                  <Check className="size-2.5 text-emerald-300" />
                                  <span>Added</span>
                                </>
                              ) : (
                                <>
                                  <Plus className="size-2.5" />
                                  <span>{quantity > 1 ? `${quantity}× MAIN` : 'MAIN'}</span>
                                </>
                              )}
                            </button>
                          </BrewTooltip>

                          {onAddToSideboard && (
                            <BrewTooltip
                              title="Add to Sideboard (+SIDE)"
                              badge={`${quantity}×`}
                              description={`Add ${quantity} cop${quantity === 1 ? 'y' : 'ies'} of ${sc.name} to your Sideboard (or Command / Companion zone).`}
                              accent="amber"
                            >
                              <button
                                type="button"
                                onClick={() => handleAddScryfallResult(sc, true)}
                                className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded bg-amber-600/75 hover:bg-amber-500 text-white text-[10px] font-semibold border border-amber-400/40 transition-colors"
                              >
                                {addedResultKey === sbKey ? (
                                  <>
                                    <Check className="size-2.5 text-emerald-200" />
                                    <span>Added</span>
                                  </>
                                ) : (
                                  <span>{quantity > 1 ? `+${quantity} SIDE` : '+SIDE'}</span>
                                )}
                              </button>
                            </BrewTooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* COLLAPSIBLE TUNING DRAWER: Curve Targets, Companion Filters, Play/Draw & Turn */}
      {toolbar && showTuning && (
        <div className="bg-slate-900/90 border border-amber-500/25 rounded-xl px-3 py-2.5 shadow-xl animate-in fade-in duration-150">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {/* Group 1: Deck & Curve Targets */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400/80">Targets:</span>

              <BrewTooltip
                title="Target Deck Size"
                badge={`${toolbar.targetDeckSize} cards`}
                description="Total maindeck card count used to generate your ideal mana curve slots. Tap − / + (or hold to rapid-step) or type directly."
                detail="Standard/Modern: 60 | Commander: 99 | Limited: 40 | Yorion: 80"
                accent="purple"
              >
                <NumericStepper
                  label="DECK"
                  value={toolbar.targetDeckSize}
                  onChange={(val) => toolbar.setTargetDeckSize(val)}
                  min={40}
                  max={250}
                  step={1}
                  decimals={0}
                  valueClassName="text-white"
                  inputWidthClassName="w-9"
                />
              </BrewTooltip>

              <BrewTooltip
                title="Target Land Count"
                badge={`${toolbar.effectiveTargetLands} lands`}
                description="Ideal number of lands for your deck. Tap − / + (or hold) to adjust; nonland spell slots and mana weight update automatically."
                detail={`Leaves ${toolbar.targetDeckSize - toolbar.effectiveTargetLands} nonland spell slots`}
                accent="amber"
              >
                <NumericStepper
                  label="LANDS"
                  value={toolbar.effectiveTargetLands}
                  onChange={(val) => toolbar.setTargetLands(val)}
                  min={0}
                  max={100}
                  step={1}
                  decimals={0}
                  valueClassName="text-amber-300"
                  inputWidthClassName="w-8"
                />
              </BrewTooltip>

              <BrewTooltip
                title="Target Weight-Per-Land Ratio (WPL)"
                badge={toolbar.buildTargetRatio.toFixed(2)}
                description="Total spell mana value divided by your land count. Tap − / + for 0.01 steps (hold to accelerate, Shift+click for 0.10) or type any ratio."
                detail="~2.2–2.6: Lean Aggro | ~2.8–3.2: Midrange | ~3.4+: Ramp / Control"
                accent="cyan"
              >
                <NumericStepper
                  label="RATIO"
                  value={Math.round(toolbar.buildTargetRatio * 100) / 100}
                  onChange={(val) => toolbar.setTargetRatio(val)}
                  min={0}
                  max={99.99}
                  step={0.01}
                  decimals={2}
                  valueClassName="text-cyan-300"
                  inputWidthClassName="w-12"
                />
              </BrewTooltip>
            </div>

            <span className="hidden md:inline-block w-px h-5 bg-white/10" />

            {/* Group 2: Curve Filters & Grouping */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400/80">Curve Filters:</span>

              <BrewTooltip
                title="Companion Mana Value Parity"
                badge={toolbar.mvParity === 'odds' ? 'Odds Only' : toolbar.mvParity === 'evens' ? 'Evens Only' : 'All MVs'}
                description="Restrict ideal curve slots to Odd Mana Values (for Obosh, the Preypiercer) or Even Mana Values (for Gyruda, Doom of Depths)."
                detail="Click to cycle: ALL → Odds → Evens"
                accent="purple"
              >
                <button
                  type="button"
                  onClick={() => toolbar.setMvParity(prev => prev === 'all' ? 'odds' : prev === 'odds' ? 'evens' : 'all')}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                    toolbar.mvParity === 'odds'
                      ? 'bg-orange-500/30 text-orange-300 border-orange-500/40'
                      : toolbar.mvParity === 'evens'
                      ? 'bg-blue-500/30 text-blue-300 border-blue-500/40'
                      : 'bg-white/5 text-white/60 border-white/10 hover:text-white'
                  }`}
                >
                  Parity: {toolbar.mvParity === 'odds' ? 'Odds' : toolbar.mvParity === 'evens' ? 'Evens' : 'All'}
                </button>
              </BrewTooltip>

              <BrewTooltip
                title="Zero-Mana (0-MV) Curve Slots"
                badge={toolbar.excludeZero ? 'Excluded' : 'Included'}
                description="Toggle whether the ideal curve generator assigns slots to 0-mana spells (like Moxen, Mishra's Bauble, or Pacts)."
                detail={toolbar.excludeZero ? 'Currently starting curve slots at MV 1+' : 'Currently including MV 0 in curve bell'}
                accent={toolbar.excludeZero ? 'red' : 'green'}
              >
                <button
                  type="button"
                  onClick={() => toolbar.setExcludeZero(prev => !prev)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                    toolbar.excludeZero
                      ? 'bg-red-500/25 text-red-300 border-red-500/40'
                      : 'bg-emerald-500/25 text-emerald-300 border-emerald-500/40'
                  }`}
                >
                  0-MV: {toolbar.excludeZero ? 'Off' : 'On'}
                </button>
              </BrewTooltip>

              {toolbar.viewMode !== 'dynamic' && (
                <BrewTooltip
                  title="Group Deck Cards By"
                  badge={toolbar.groupBy.toUpperCase()}
                  description="Choose how cards are categorized in List and Grid views (by Card Type, Mana Value, Color, or Alphabetically)."
                  accent="purple"
                >
                  <select
                    value={toolbar.groupBy}
                    onChange={(e) => toolbar.setGroupBy(e.target.value)}
                    className="bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-xs text-white focus:outline-none"
                  >
                    <option value="type" className="bg-slate-900">Group: Type</option>
                    <option value="mv" className="bg-slate-900">Group: MV</option>
                    <option value="color" className="bg-slate-900">Group: Color</option>
                    <option value="name" className="bg-slate-900">Group: A–Z</option>
                  </select>
                </BrewTooltip>
              )}
            </div>

            <span className="hidden md:inline-block w-px h-5 bg-white/10" />

            {/* Group 3: Probability Evaluation Context */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400/80">Odds Eval:</span>

              <BrewTooltip
                title="On the Play vs. On the Draw"
                badge={toolbar.onThePlay ? 'Play (7 cards T1)' : 'Draw (8 cards T1)'}
                description="Toggles whether your deck probabilities assume you go first (On the Play) or second (On the Draw, drawing +1 extra card)."
                accent="purple"
              >
                <button
                  type="button"
                  onClick={() => toolbar.setOnThePlay(!toolbar.onThePlay)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                    toolbar.onThePlay
                      ? 'bg-purple-500/30 text-purple-300 border-purple-500/40'
                      : 'bg-amber-500/30 text-amber-300 border-amber-500/40'
                  }`}
                >
                  {toolbar.onThePlay ? 'On Play' : 'On Draw'}
                </button>
              </BrewTooltip>

              <BrewTooltip
                title="Target Evaluation Turn"
                badge={`Turn ${toolbar.probTargetTurn}`}
                description="Sets the reference turn used across your deck to evaluate 'Seeable by Turn T' and combined playability odds."
                detail={`Seeing ${toolbar.onThePlay ? 6 + toolbar.probTargetTurn : 7 + toolbar.probTargetTurn} cards by Turn ${toolbar.probTargetTurn}`}
                accent="cyan"
              >
                <NumericStepper
                  label="TURN"
                  value={toolbar.probTargetTurn}
                  onChange={(val) => toolbar.setProbTargetTurn(Math.max(1, val))}
                  min={1}
                  max={20}
                  step={1}
                  decimals={0}
                  valueClassName="text-white"
                  inputWidthClassName="w-7"
                />
              </BrewTooltip>
            </div>
          </div>
        </div>
      )}

      {/* ROW 2: Streamlined Stats Bar — 2 numbers (Actual / Target) in Base mode, 3 numbers (Base / Target / Match) in Match mode */}
      {stats && toolbar && (() => {
        const isPlanActive = toolbar.activePlan !== 'base';
        const actualLands = stats.lands;
        const targetLands = toolbar.effectiveTargetLands;
        const planLands = stats.planStats?.lands ?? stats.lands;

        const actualSpells = stats.nonlands;
        const targetSpells = stats.targetNonlands;
        const planSpells = stats.planStats?.spells ?? stats.nonlands;

        const actualWeight = Math.round(stats.weight);
        const targetWeight = stats.targetWeight;
        const planWeight = Math.round(stats.planStats?.weight ?? stats.weight);

        const actualCurve = stats.curve;
        const targetCurve = stats.targetCurve;
        const planCurve = stats.planStats?.curve ?? stats.curve;

        const actualRatio = stats.actualRatio ?? 0;
        const targetRatio = toolbar.buildTargetRatio;
        const planRatio = stats.planStats?.ratio ?? actualRatio;

        return (
          <div className="flex items-center justify-center gap-1.5 sm:gap-3 flex-wrap bg-white/[0.04] border border-white/10 rounded-lg px-2.5 py-1.5 w-fit mx-auto max-w-full">
            {/* Format Legend Pill */}
            <BrewTooltip
              title="Deck Metrics Legend"
              badge={isPlanActive ? `Base / Target / ${matchLabel}` : 'Actual / Target'}
              description={
                isPlanActive
                  ? 'White = Base Deck | Amber = Ideal Target | Cyan/Green = Active Matchup Plan.'
                  : 'First number is your current deck value; Amber number is your ideal target. Turns green when matched!'
              }
              accent="slate"
            >
              <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/40 border border-white/10 cursor-help">
                {isPlanActive ? `Base/Tgt/${matchLabel}` : 'Actual / Target'}
              </span>
            </BrewTooltip>

            {/* LANDS */}
            <BrewTooltip
              title="Land Count"
              badge={`${isPlanActive ? planLands : actualLands} / ${targetLands} Lands`}
              description="Total mana-producing lands in your maindeck compared to your Target Land count."
              detail={
                isPlanActive
                  ? `Base: ${actualLands} | Target: ${targetLands} | ${matchLabel}: ${planLands}`
                  : actualLands === targetLands
                  ? '✦ Exact match with your target land count'
                  : actualLands < targetLands
                  ? `Need ${targetLands - actualLands} more land(s) to hit target`
                  : `${actualLands - targetLands} land(s) over target`
              }
              accent="amber"
            >
              <span className="text-[11px] sm:text-xs whitespace-nowrap cursor-help px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors">
                <span className="text-white/50 mr-1">LANDS:</span>
                <span className={ !isPlanActive && actualLands === targetLands ? 'text-green-400 font-semibold' : 'text-white font-semibold' }>
                  {actualLands}
                </span>
                <span className="text-white/30 mx-0.5">/</span>
                <span className="text-amber-400 font-medium">{targetLands}</span>
                {isPlanActive && (
                  <>
                    <span className="text-white/30 mx-0.5">/</span>
                    <span className={planLands === targetLands ? 'text-green-400 font-semibold' : 'text-cyan-400 font-semibold'}>
                      {planLands}
                    </span>
                  </>
                )}
              </span>
            </BrewTooltip>

            {/* NONLANDS */}
            <BrewTooltip
              title="Nonland Spells"
              badge={`${isPlanActive ? planSpells : actualSpells} / ${targetSpells} Spells`}
              description="Total nonland cards (creatures, instants, sorceries, artifacts, enchantments, planeswalkers) in your maindeck."
              detail={
                isPlanActive
                  ? `Base: ${actualSpells} | Target: ${targetSpells} | ${matchLabel}: ${planSpells}`
                  : `Target Deck (${toolbar.targetDeckSize}) − Target Lands (${targetLands}) = ${targetSpells} spells`
              }
              accent="purple"
            >
              <span className="text-[11px] sm:text-xs whitespace-nowrap cursor-help px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors">
                <span className="text-white/50 mr-1">NONLANDS:</span>
                <span className={!isPlanActive && actualSpells === targetSpells ? 'text-green-400 font-semibold' : 'text-white font-semibold'}>
                  {actualSpells}
                </span>
                <span className="text-white/30 mx-0.5">/</span>
                <span className="text-amber-400 font-medium">{targetSpells}</span>
                {isPlanActive && (
                  <>
                    <span className="text-white/30 mx-0.5">/</span>
                    <span className={planSpells === targetSpells ? 'text-green-400 font-semibold' : 'text-cyan-400 font-semibold'}>
                      {planSpells}
                    </span>
                  </>
                )}
              </span>
            </BrewTooltip>

            {/* WEIGHT */}
            <BrewTooltip
              title="Total Deck Mana Weight"
              badge={`${isPlanActive ? planWeight : actualWeight} / ${targetWeight}`}
              description="Sum of the Mana Value (MV) of every nonland spell in your deck. Keeping actual weight close to target weight prevents mana screw/flood."
              detail={`Formula: Σ(Card MV × Copies) | Target = ${targetLands} Lands × ${targetRatio.toFixed(2)} Ratio`}
              accent="cyan"
            >
              <span className="text-[11px] sm:text-xs whitespace-nowrap cursor-help px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors">
                <span className="text-white/50 mr-1">WEIGHT:</span>
                <span className={!isPlanActive && actualWeight === targetWeight ? 'text-green-400 font-semibold' : 'text-white font-semibold'}>
                  {actualWeight}
                </span>
                <span className="text-white/30 mx-0.5">/</span>
                <span className="text-amber-400 font-medium">{targetWeight}</span>
                {isPlanActive && (
                  <>
                    <span className="text-white/30 mx-0.5">/</span>
                    <span className={planWeight === targetWeight ? 'text-green-400 font-semibold' : 'text-cyan-400 font-semibold'}>
                      {planWeight}
                    </span>
                  </>
                )}
              </span>
            </BrewTooltip>

            {/* CURVE */}
            <BrewTooltip
              title="Average Mana Value (Curve)"
              badge={`${(isPlanActive ? planCurve : actualCurve).toFixed(2)} Avg MV`}
              description="Average mana cost of a nonland spell in your deck (Total Weight ÷ Nonland Spells). Lower means faster; higher means slower/heavier."
              detail={`Actual: ${actualCurve.toFixed(2)} | Target: ${targetCurve.toFixed(2)}${isPlanActive ? ` | ${matchLabel}: ${planCurve.toFixed(2)}` : ''}`}
              accent="green"
            >
              <span className="text-[11px] sm:text-xs whitespace-nowrap cursor-help px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors">
                <span className="text-white/50 mr-1">CURVE:</span>
                <span className={!isPlanActive && Math.abs(actualCurve - targetCurve) < 0.05 ? 'text-green-400 font-semibold' : 'text-white font-semibold'}>
                  {actualCurve.toFixed(2)}
                </span>
                <span className="text-white/30 mx-0.5">/</span>
                <span className="text-amber-400 font-medium">{targetCurve.toFixed(2)}</span>
                {isPlanActive && (
                  <>
                    <span className="text-white/30 mx-0.5">/</span>
                    <span className={Math.abs(planCurve - targetCurve) < 0.05 ? 'text-green-400 font-semibold' : 'text-cyan-400 font-semibold'}>
                      {planCurve.toFixed(2)}
                    </span>
                  </>
                )}
              </span>
            </BrewTooltip>

            {/* RATIO */}
            <BrewTooltip
              title="Weight-Per-Land Ratio (WPL)"
              badge={`${stats.actualRatio ? (isPlanActive ? planRatio : actualRatio).toFixed(2) : '--'} WPL`}
              description="How much total spell mana weight each land in your deck has to support (Weight ÷ Lands). A healthy constructed deck typically sits between 2.50 and 3.50."
              detail="~2.5: Aggro | ~3.0: Midrange | ~3.5+: Ramp/Control (Click 'Tune' to adjust target)"
              accent="amber"
            >
              <span className="text-[11px] sm:text-xs whitespace-nowrap cursor-help px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors">
                <span className="text-white/50 mr-1">RATIO:</span>
                <span className={!isPlanActive && Math.abs(actualRatio - targetRatio) < 0.15 ? 'text-green-400 font-semibold' : 'text-white font-semibold'}>
                  {stats.actualRatio ? actualRatio.toFixed(2) : '--'}
                </span>
                <span className="text-white/30 mx-0.5">/</span>
                <span className="text-amber-400 font-medium">{targetRatio.toFixed(2)}</span>
                {isPlanActive && (
                  <>
                    <span className="text-white/30 mx-0.5">/</span>
                    <span className={Math.abs(planRatio - targetRatio) < 0.15 ? 'text-green-400 font-semibold' : 'text-cyan-400 font-semibold'}>
                      {planRatio ? planRatio.toFixed(2) : '--'}
                    </span>
                  </>
                )}
              </span>
            </BrewTooltip>
          </div>
        );
      })()}

      {error && (
        <div className="text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded">
          {error}
        </div>
      )}
    </form>
  );
}
